import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import TermsAcceptance from "@/components/TermsAcceptance";
import { 
  Snowflake, 
  ArrowLeft, 
  Ticket, 
  PartyPopper,
  User,
  Mail,
  Phone,
  Minus,
  Plus,
  CreditCard,
  CheckCircle2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const TICKET_TYPES = {
  vip: {
    name: "Silvester VIP Ticket",
    emoji: "👑",
    price: 49.00,
    time: "Ab 18:00 Uhr",
    features: [
      "🍽️ Buffet & Eintritt inklusive",
      "🪑 Sitzplatz reserviert",
      "🥂 Getränke separat"
    ],
    color: "amber"
  },
  normal: {
    name: "Silvester Normal Ticket",
    emoji: "🎉",
    price: 15.00,
    time: "Ab 21:00 Uhr",
    features: [
      "🎟️ Eintritt",
      "🍹 Getränke & Verpflegung separat"
    ],
    color: "sky"
  }
};

const SilvesterBooking = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ticketTypeParam = searchParams.get('type') || 'vip';
  
  const [ticketType, setTicketType] = useState(ticketTypeParam);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [vipAvailability, setVipAvailability] = useState(null); // {capacity_open, free, occupied}
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    ticketCount: 2
  });

  const currentTicket = TICKET_TYPES[ticketType];
  const totalPrice = formData.ticketCount * currentTicket.price;
  const SERVICE_FEE = 2.00;
  const grandTotal = totalPrice + SERVICE_FEE;

  useEffect(() => {
    if (ticketTypeParam && TICKET_TYPES[ticketTypeParam]) {
      setTicketType(ticketTypeParam);
    }
  }, [ticketTypeParam]);

  // Fetch VIP availability when type=vip is selected
  useEffect(() => {
    if (ticketType !== "vip") return;
    let alive = true;
    const load = async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/silvester/vip-availability`;
        const res = await fetch(url);
        const data = await res.json();
        if (alive) setVipAvailability(data);
      } catch {
        // ignore
      }
    };
    load();
    return () => { alive = false; };
  }, [ticketType, bookingComplete]);

  const adjustTickets = (delta) => {
    setFormData(prev => ({
      ...prev,
      ticketCount: Math.max(1, Math.min(20, prev.ticketCount + delta))
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error("Bitte fülle alle Felder aus");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/silvester/book`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        ticket_count: formData.ticketCount,
        ticket_type: ticketType
      });
      
      setBookingData(response.data);
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Buchung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const createOrder = async () => {
    try {
      const response = await axios.post(`${API}/paypal/create-order`, {
        booking_id: bookingData.id,
        booking_type: "silvester"
      });
      return response.data.id;
    } catch (error) {
      toast.error("Fehler bei der Erstellung der PayPal-Bestellung");
      throw error;
    }
  };

  const onApprove = async (data) => {
    try {
      await axios.post(`${API}/paypal/capture-order`, {
        order_id: data.orderID,
        booking_id: bookingData.id,
        booking_type: "silvester"
      });
      
      setBookingComplete(true);
      toast.success("Zahlung erfolgreich! Buchung bestätigt.");
    } catch (error) {
      toast.error("Fehler bei der Zahlungsabwicklung");
    }
  };

  if (bookingComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-sky-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full border-0 shadow-xl" data-testid="booking-success-card">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Playfair Display' }}>
              Buchung bestätigt!
            </h2>
            <p className="text-slate-600 mb-6">
              Vielen Dank für deine Buchung. Eine Bestätigung wurde an {formData.email} gesendet.
            </p>
            
            <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
              <div className="flex justify-between mb-2">
                <span className="text-slate-500">Buchungsnummer</span>
                <span className="font-mono text-sm">{bookingData?.id?.slice(0, 8)}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-slate-500">Ticket-Typ</span>
                <span className="font-semibold">{currentTicket.emoji} {currentTicket.name}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-slate-500">Anzahl Tickets</span>
                <span className="font-semibold">{formData.ticketCount}</span>
              </div>
              {ticketType === 'vip' && bookingData?.seat_numbers?.length > 0 && (
                <div className="flex justify-between mb-2" data-testid="success-seats-row">
                  <span className="text-slate-500">Sitzplätze</span>
                  <span className="font-semibold text-amber-700">
                    {(() => {
                      const s = [...bookingData.seat_numbers].sort((a, b) => a - b);
                      if (s.length > 1 && s[s.length - 1] - s[0] === s.length - 1) {
                        return `${s[0]}–${s[s.length - 1]}`;
                      }
                      return s.join(', ');
                    })()}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-slate-500">Gesamtpreis</span>
                <span className="font-semibold text-sky-600">{grandTotal.toFixed(2)} €</span>
              </div>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
              <p className="text-green-800 text-sm">
                📧 Nach der Zahlung erhältst du dein Ticket per E-Mail mit QR-Code.<br />
                <strong>Bitte beim Einlass bereithalten (Handy oder Ausdruck).</strong>
              </p>
            </div>
            
            <Link to="/">
              <Button 
                className="w-full h-12 rounded-full bg-sky-500 hover:bg-sky-600"
                data-testid="back-home-btn"
              >
                Zurück zur Startseite
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-sky-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-slate-600 hover:text-sky-500 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Zurück</span>
          </Link>
          <div className="flex items-center gap-3">
            <Snowflake className="w-6 h-6 text-sky-500" />
            <span className="font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display' }}>
              Grömitzer Eiszeit
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Ticket Selection & Info */}
          <div className="lg:sticky lg:top-24 lg:h-fit">
            {/* Ticket Type Selector */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <button
                onClick={() => setTicketType('vip')}
                className={`p-4 rounded-2xl border-2 transition-all ${
                  ticketType === 'vip' 
                    ? 'border-amber-500 bg-amber-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
                data-testid="select-vip-btn"
              >
                <span className="text-3xl block mb-2">👑</span>
                <span className="font-semibold text-slate-900">VIP Ticket</span>
                <p className="text-amber-600 font-bold mt-1">49,00 €</p>
                {ticketType === 'vip' && vipAvailability && (
                  <p
                    className={`text-xs mt-2 font-medium ${vipAvailability.free <= 20 ? 'text-red-600' : 'text-emerald-700'}`}
                    data-testid="vip-availability-info"
                  >
                    {vipAvailability.free > 0
                      ? `Noch ${vipAvailability.free} von ${vipAvailability.capacity_open} VIP-Sitzplätzen frei`
                      : 'Aktuell ausverkauft'}
                  </p>
                )}
              </button>
              <button
                onClick={() => setTicketType('normal')}
                className={`p-4 rounded-2xl border-2 transition-all ${
                  ticketType === 'normal' 
                    ? 'border-sky-500 bg-sky-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
                data-testid="select-normal-btn"
              >
                <span className="text-3xl block mb-2">🎉</span>
                <span className="font-semibold text-slate-900">Normal Ticket</span>
                <p className="text-sky-600 font-bold mt-1">15,00 €</p>
              </button>
            </div>

            <div className="relative rounded-3xl overflow-hidden shadow-2xl mb-8">
              <img 
                src="https://images.unsplash.com/photo-1467810563316-b5476525c0f9?w=800&q=80" 
                alt="Silvester" 
                className="w-full h-[300px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className={`inline-flex items-center gap-2 ${ticketType === 'vip' ? 'bg-amber-500 text-slate-900' : 'bg-sky-500 text-white'} rounded-full px-4 py-2 mb-4`}>
                  <PartyPopper className="w-5 h-5" />
                  <span className="font-semibold">31. Dezember 2026 {currentTicket.time}</span>
                </div>
                <h1 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display' }}>
                  {currentTicket.emoji} {currentTicket.name}
                </h1>
                <p className="text-sky-100">
                  Feiert den Jahreswechsel im Almhüttenzelt mit Blick auf das Feuerwerk über der Ostsee!
                </p>
              </div>
            </div>
            
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display' }}>
                  Im Ticket enthalten:
                </h3>
                <ul className="space-y-3 text-slate-600">
                  {currentTicket.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-4 border-t border-slate-100">
                  <p className="text-amber-600 text-sm font-medium">
                    📅 Ticketvorverkauf {ticketType === 'vip' ? 'bis' : 'ab'} 15.12.2026
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Booking Form */}
          <div>
            {/* Progress Steps */}
            <div className="flex items-center gap-4 mb-8">
              <div className={`flex items-center gap-2 ${step >= 1 ? 'text-sky-600' : 'text-slate-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= 1 ? 'bg-sky-500 text-white' : 'bg-slate-200'}`}>
                  1
                </div>
                <span className="font-medium">Details</span>
              </div>
              <div className="flex-1 h-0.5 bg-slate-200">
                <div className={`h-full bg-sky-500 transition-all ${step >= 2 ? 'w-full' : 'w-0'}`} />
              </div>
              <div className={`flex items-center gap-2 ${step >= 2 ? 'text-sky-600' : 'text-slate-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= 2 ? 'bg-sky-500 text-white' : 'bg-slate-200'}`}>
                  2
                </div>
                <span className="font-medium">Zahlung</span>
              </div>
            </div>

            {step === 1 && (
              <Card className="border-0 shadow-xl" data-testid="booking-form-card">
                <CardHeader>
                  <CardTitle className="text-2xl" style={{ fontFamily: 'Playfair Display' }}>
                    Deine Daten
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Ticket Count */}
                  <div className={`rounded-xl p-6 ${ticketType === 'vip' ? 'bg-amber-50' : 'bg-sky-50'}`}>
                    <Label className="text-slate-700 mb-4 block">Anzahl Tickets ({currentTicket.name})</Label>
                    <div className="flex items-center justify-between">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => adjustTickets(-1)}
                        disabled={formData.ticketCount <= 1}
                        className="h-12 w-12 rounded-full"
                        data-testid="decrease-tickets-btn"
                      >
                        <Minus className="w-5 h-5" />
                      </Button>
                      <div className="text-center">
                        <span className="text-4xl font-bold text-slate-900" data-testid="ticket-count">
                          {formData.ticketCount}
                        </span>
                        <p className="text-sm text-slate-500 mt-1">
                          {formData.ticketCount === 1 ? 'Ticket' : 'Tickets'}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => adjustTickets(1)}
                        disabled={formData.ticketCount >= 20 || (ticketType === 'vip' && vipAvailability && formData.ticketCount >= vipAvailability.free)}
                        className="h-12 w-12 rounded-full"
                        data-testid="increase-tickets-btn"
                      >
                        <Plus className="w-5 h-5" />
                      </Button>
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-200 flex justify-between items-center">
                      <span className="text-slate-600">{formData.ticketCount} x {currentTicket.price.toFixed(2)} €</span>
                      <span className={`text-2xl font-bold ${ticketType === 'vip' ? 'text-amber-600' : 'text-sky-600'}`} data-testid="total-price">
                        {totalPrice.toFixed(2)} €
                      </span>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="name" className="text-slate-700">Name</Label>
                      <div className="relative mt-2">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Max Mustermann"
                          className="h-12 pl-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-sky-500/20"
                          data-testid="name-input"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="email" className="text-slate-700">E-Mail</Label>
                      <div className="relative mt-2">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="max@example.de"
                          className="h-12 pl-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-sky-500/20"
                          data-testid="email-input"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="phone" className="text-slate-700">Telefon</Label>
                      <div className="relative mt-2">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="+49 123 456789"
                          className="h-12 pl-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-sky-500/20"
                          data-testid="phone-input"
                        />
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={handleSubmit}
                    disabled={loading}
                    className={`w-full h-14 rounded-full font-semibold text-lg shadow-lg ${
                      ticketType === 'vip' 
                        ? 'bg-amber-500 hover:bg-amber-600 text-slate-900' 
                        : 'bg-sky-500 hover:bg-sky-600 text-white shadow-sky-500/20'
                    }`}
                    data-testid="continue-to-payment-btn"
                  >
                    {loading ? "Wird verarbeitet..." : "Weiter zur Zahlung"}
                  </Button>
                </CardContent>
              </Card>
            )}

            {step === 2 && (
              <Card className="border-0 shadow-xl" data-testid="payment-card">
                <CardHeader>
                  <CardTitle className="text-2xl" style={{ fontFamily: 'Playfair Display' }}>
                    Zahlung
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Order Summary */}
                  <div className="bg-slate-50 rounded-xl p-6">
                    <h4 className="font-semibold text-slate-900 mb-4">Zusammenfassung</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-slate-600">{currentTicket.emoji} {currentTicket.name} x {formData.ticketCount}</span>
                        <span className="font-medium">{totalPrice.toFixed(2)} €</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Servicegebühr</span>
                        <span className="text-slate-700">{SERVICE_FEE.toFixed(2)} €</span>
                      </div>
                      <div className="border-t border-slate-200 pt-3 flex justify-between">
                        <span className="font-semibold text-slate-900">Gesamt</span>
                        <span className={`text-2xl font-bold ${ticketType === 'vip' ? 'text-amber-600' : 'text-sky-600'}`} data-testid="silvester-grand-total">{grandTotal.toFixed(2)} €</span>
                      </div>
                    </div>
                  </div>

                  {/* Payment Info Box */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">💳</span>
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-1">Zahlungsarten</h4>
                        <p className="text-slate-700 font-medium">PayPal</p>
                        <p className="text-slate-600 text-sm mt-1">
                          (PayPal, Kreditkarte, Lastschrift oder Überweisung über PayPal)
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* AGB Akzeptanz */}
                  <TermsAcceptance
                    checked={agbAccepted}
                    onChange={setAgbAccepted}
                    testId="silvester-agb"
                  />

                  {/* PayPal Buttons */}
                  <div className="space-y-4">
                    {PAYPAL_CLIENT_ID && (
                      <div className={agbAccepted ? "" : "opacity-50 pointer-events-none"}>
                        <PayPalScriptProvider options={{ 
                          clientId: PAYPAL_CLIENT_ID,
                          currency: "EUR",
                          locale: "de_DE"
                        }}>
                          <PayPalButtons
                            style={{ 
                              layout: "vertical",
                              shape: "pill",
                              label: "pay"
                            }}
                            disabled={!agbAccepted}
                            createOrder={createOrder}
                            onApprove={onApprove}
                            onError={(err) => {
                              console.error("PayPal Error:", err);
                              toast.error("PayPal-Fehler. Bitte versuche es erneut.");
                            }}
                          />
                        </PayPalScriptProvider>
                      </div>
                    )}
                    
                    {!agbAccepted && (
                      <p className="text-sm text-slate-500 text-center">
                        Bitte AGB akzeptieren, um die Zahlung fortzusetzen.
                      </p>
                    )}
                    
                    {/* Confirmation Info */}
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                      <p className="text-green-800 text-sm">
                        📧 Nach der Zahlung erhältst du dein Ticket per E-Mail mit QR-Code.<br />
                        <strong>Bitte beim Einlass bereithalten (Handy oder Ausdruck).</strong>
                      </p>
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    onClick={() => setStep(1)}
                    className="w-full"
                    data-testid="back-to-details-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Zurück zu den Details
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SilvesterBooking;
